

import React, { useState, useEffect } from 'react';
import { LogoPlaceholder, LoadingSpinner } from './icons';
import { getPlatformSettings } from '../services/apiService';
import { getLegalContent } from '../services/apiService';
import LegalModal from './LegalModal';
import { LegalContent, PlatformSettings } from '../types';

interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
  verifyStatus?: 'checking' | 'success' | 'error' | null;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, error, isLoading, onSwitchToRegister, onForgotPassword, verifyStatus }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [legalContent, setLegalContent] = useState<LegalContent | null>(null);
  const [modalContent, setModalContent] = useState<{title: string, content: string} | null>(null);
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings | null>(null);


  useEffect(() => {
    const fetchLegal = async () => {
        const content = await getLegalContent();
        setLegalContent(content);
    };
     const fetchSettings = async () => {
        const settings = await getPlatformSettings();
        setPlatformSettings(settings);
    };
    fetchLegal();
    fetchSettings();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(email, password);
  };

  const openModal = (type: 'privacy' | 'terms') => {
      if (!legalContent) return;
      if (type === 'privacy') {
          setModalContent({ title: 'Privacy Policy', content: legalContent.privacyPolicy });
      } else {
          setModalContent({ title: 'Terms and Conditions', content: legalContent.termsAndConditions });
      }
  };

  return (
    <>
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {platformSettings?.loginPageLogo ? (
            <img src={platformSettings.loginPageLogo} alt="Login Logo" className="mx-auto h-16 w-auto object-contain" />
        ) : (
            <LogoPlaceholder className="mx-auto h-16 w-auto text-primary" />
        )}
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {platformSettings?.loginPageTitle || "Taxi Compliance Registry Portal"}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to your network account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl ring-1 ring-gray-900/10 sm:rounded-lg sm:px-10">
          {verifyStatus === 'success' && (
            <p className="mb-6 text-sm text-green-700 p-3 bg-green-50 rounded">Email verified. You can sign in once your organisation is approved.</p>
          )}
          {verifyStatus === 'error' && (
            <p className="mb-6 text-sm text-red-600 p-3 bg-red-50 rounded">That verification link is invalid or has expired.</p>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm"
                />
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:bg-gray-400"
              >
                {isLoading ? <LoadingSpinner className="w-5 h-5" /> : 'Sign in'}
              </button>
            </div>
            <button
              type="button"
              onClick={onForgotPassword}
              className="w-full text-center text-sm text-gray-500 hover:text-primary"
            >
              Forgot your password?
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  New to the platform?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={onSwitchToRegister}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                Register a new Taxi Network
              </button>
            </div>
          </div>

        </div>
        <div className="mt-6 text-center text-xs text-gray-500">
            <p>By logging in or registering, you agree to our</p>
            <button onClick={() => openModal('terms')} className="underline hover:text-primary">Terms & Conditions</button> and 
            <button onClick={() => openModal('privacy')} className="underline hover:text-primary ml-1">Privacy Policy</button>.
        </div>
      </div>
    </div>
    {modalContent && <LegalModal title={modalContent.title} content={modalContent.content} onClose={() => setModalContent(null)} />}
    </>
  );
};

export default LoginPage;